/*
 * PerfBoard — the BOARD MODEL for /perfboard-simulator: a soldered perf/strip board.
 *
 * Deliberately UI-free and DOM-free so it can run under `node --test`
 * (tests/node/perfboard-board.test.mjs). ui.js owns canvas + gestures; this file
 * owns geometry, the electrical model, and the autorouter.
 *
 * ── Electrical model (this is the whole point, and it differs from the breadboard) ──
 * A breadboard has fixed internal strips. A perfboard has none — YOU make every
 * connection by soldering. So the model is:
 *
 *   - every hole carries TWO pads: bottom (layer 0) and top (layer 1)
 *   - a pad is connected to a NEIGHBOURING pad on the SAME layer only if a trace
 *     (solder bridge / wire link) was drawn between them
 *   - the two pads OF ONE HOLE are connected only when something physically passes
 *     through the hole: a component lead, or a via (a soldered through-wire)
 *
 * That last rule is what makes a two-sided board worth anything: an empty hole can
 * carry net A underneath and net B on top without shorting, which is exactly how a
 * hand-built double-sided board dodges a crossing. On a one-sided board layer 1 does
 * not exist and every trace runs on the bottom.
 *
 * Stripboard (veroboard) is the same grid with the bottom row-strips PRE-joined; a
 * "cut" breaks one strip segment with a drill bit. Cuts apply to layer 0 only.
 *
 * ── Routing constraint ──
 * Unit orthogonal edges between adjacent holes can never cross each other, so the
 * only way two nets collide is by SHARING A PAD. The router is therefore a plain
 * node-exclusion maze search: a pad belongs to at most one net.
 *
 * Pure ES5 — this file is served raw (no build step) and must parse in old Safari:
 * `function(){}` only, no arrows, no `??`, no `?.`, no template literals.
 */
(function (root) {
  'use strict';

  /* ------------------------------------------------------------------ *
   * Board geometry                                                      *
   * ------------------------------------------------------------------ */

  // 2.54 mm pitch drawn at 22 world px; everything else is derived from it so the
  // board scales as one piece.
  var PITCH = 22;
  var MARGIN = 26;          // board edge → first hole centre

  /**
   * @param {object} opts {cols, rows, kind:'perf'|'strip', sides:1|2, plated:bool}
   */
  function Board(opts) {
    opts = opts || {};
    this.cols = clampInt(opts.cols, 8, 60, 24);
    this.rows = clampInt(opts.rows, 6, 40, 18);
    this.kind = opts.kind === 'strip' ? 'strip' : 'perf';
    this.sides = opts.sides === 2 ? 2 : 1;
    // Plated-through двусторонка (Ivan 05.08: «там эти точки с 2 сторон
    // соединены — пересекаешь со второй стороны, коротишь с первой»): the
    // factory plating fuses BOTH pads of EVERY hole. That is the common cheap
    // double-sided perfboard; the unplated board (independent pads) stays the
    // default. Consequence Ivan described is real here: a continuous run on one
    // side cannot be crossed on the other — the cure is an insulated jumper.
    this.plated = this.sides === 2 && opts.plated === true;

    this.traces = [];       // {layer, a, b}  a<b, always ADJACENT holes
    this.cuts = [];         // {a, b}         strip cut (layer 0 only), adjacent holes
    this.vias = [];         // holeIdx        soldered through-wire, bridges both pads
    this.components = [];   // {id,type,ref,holes:[...],props:{}}
    this.notes = {};        // holeIdx -> text  a written label on a hole (see below)
    this.nextId = 1;
  }

  /*
   * Hole notes. Ivan's most-repeated request: label a hole with free text, so a part
   * the palette does not carry can still be placed on the plan — "K1 coil here", "to
   * the speaker", "9V in". A note is pure annotation: it draws and it saves, it has no
   * electrical effect. This is deliberately the escape hatch for "not every schematic
   * part exists in the palette" — you write where it goes instead of needing a symbol.
   */
  Board.prototype.setNote = function (hole, text) {
    text = String(text == null ? '' : text).slice(0, 40);
    if (text.trim() === '') { delete this.notes[hole]; return false; }
    this.notes[hole] = text;
    return true;
  };
  Board.prototype.getNote = function (hole) { return this.notes[hole] || ''; };

  function clampInt(v, lo, hi, dflt) {
    v = parseInt(v, 10);
    if (!isFinite(v)) return dflt;
    return Math.max(lo, Math.min(hi, v));
  }

  Board.PITCH = PITCH;
  Board.MARGIN = MARGIN;

  Board.prototype.holeCount = function () { return this.cols * this.rows; };
  Board.prototype.idx = function (c, r) { return r * this.cols + c; };
  Board.prototype.col = function (i) { return i % this.cols; };
  Board.prototype.row = function (i) { return Math.floor(i / this.cols); };
  Board.prototype.inBounds = function (c, r) { return c >= 0 && r >= 0 && c < this.cols && r < this.rows; };

  /** World-space centre of a hole. */
  Board.prototype.holeXY = function (i) {
    return { x: MARGIN + this.col(i) * PITCH, y: MARGIN + this.row(i) * PITCH };
  };
  Board.prototype.width = function () { return MARGIN * 2 + (this.cols - 1) * PITCH; };
  Board.prototype.height = function () { return MARGIN * 2 + (this.rows - 1) * PITCH; };

  /** Human-readable hole name, spreadsheet style: A1, B7, AA3 — matches how kits label boards. */
  Board.prototype.holeName = function (i) {
    var c = this.col(i), r = this.row(i), s = '';
    do { s = String.fromCharCode(65 + (c % 26)) + s; c = Math.floor(c / 26) - 1; } while (c >= 0);
    return s + (r + 1);
  };

  /** Nearest hole to a world point, or -1 when the point is further than `rad`. */
  Board.prototype.holeAt = function (wx, wy, rad) {
    rad = rad || PITCH * 0.55;
    var c = Math.round((wx - MARGIN) / PITCH), r = Math.round((wy - MARGIN) / PITCH);
    if (!this.inBounds(c, r)) return -1;
    var dx = wx - (MARGIN + c * PITCH), dy = wy - (MARGIN + r * PITCH);
    return (dx * dx + dy * dy <= rad * rad) ? this.idx(c, r) : -1;
  };

  /** True when a and b are orthogonally adjacent (the only edges a trace may use). */
  Board.prototype.adjacent = function (a, b) {
    if (a === b) return false;
    var dc = Math.abs(this.col(a) - this.col(b)), dr = Math.abs(this.row(a) - this.row(b));
    return (dc + dr) === 1;
  };

  Board.prototype.neighbours = function (i) {
    var c = this.col(i), r = this.row(i), out = [];
    if (c > 0) out.push(i - 1);
    if (c < this.cols - 1) out.push(i + 1);
    if (r > 0) out.push(i - this.cols);
    if (r < this.rows - 1) out.push(i + this.cols);
    return out;
  };

  /* ------------------------------------------------------------------ *
   * Traces / cuts / vias — every mutation normalises a<b so lookups are cheap *
   * ------------------------------------------------------------------ */

  function key(layer, a, b) { return layer + ':' + Math.min(a, b) + '-' + Math.max(a, b); }

  Board.prototype.maxLayer = function () { return this.sides === 2 ? 1 : 0; };

  Board.prototype.hasTrace = function (layer, a, b) {
    for (var i = 0; i < this.traces.length; i++) {
      var t = this.traces[i];
      if (t.layer === layer && Math.min(t.a, t.b) === Math.min(a, b) && Math.max(t.a, t.b) === Math.max(a, b)) return true;
    }
    return false;
  };

  /** @return {boolean} true when something changed. */
  Board.prototype.addTrace = function (layer, a, b) {
    if (layer > this.maxLayer() || layer < 0) return false;
    if (!this.adjacent(a, b)) return false;
    if (this.hasTrace(layer, a, b)) return false;
    this.traces.push({ layer: layer, a: Math.min(a, b), b: Math.max(a, b) });
    return true;
  };

  Board.prototype.removeTrace = function (layer, a, b) {
    var k = key(layer, a, b), n = this.traces.length;
    this.traces = this.traces.filter(function (t) { return key(t.layer, t.a, t.b) !== k; });
    return this.traces.length !== n;
  };

  /** Draw if absent, erase if present — one gesture for both, like a real pencil. */
  Board.prototype.toggleTrace = function (layer, a, b) {
    if (this.hasTrace(layer, a, b)) { this.removeTrace(layer, a, b); return false; }
    return this.addTrace(layer, a, b);
  };

  Board.prototype.hasCut = function (a, b) {
    var k = key(0, a, b);
    for (var i = 0; i < this.cuts.length; i++) if (key(0, this.cuts[i].a, this.cuts[i].b) === k) return true;
    return false;
  };

  /** Strip cut. Only meaningful on a stripboard, and only along a row. */
  Board.prototype.toggleCut = function (a, b) {
    if (this.kind !== 'strip') return false;
    if (!this.adjacent(a, b) || this.row(a) !== this.row(b)) return false;
    if (this.hasCut(a, b)) {
      var k = key(0, a, b);
      this.cuts = this.cuts.filter(function (c) { return key(0, c.a, c.b) !== k; });
      return false;
    }
    this.cuts.push({ a: Math.min(a, b), b: Math.max(a, b) });
    return true;
  };

  Board.prototype.hasVia = function (i) { return this.vias.indexOf(i) >= 0; };

  Board.prototype.toggleVia = function (i) {
    if (this.sides !== 2) return false;   // a via is meaningless on a one-sided board
    if (this.plated) return false;        // plating already joins every hole through
    var p = this.vias.indexOf(i);
    if (p >= 0) { this.vias.splice(p, 1); return false; }
    this.vias.push(i);
    return true;
  };

  /* ------------------------------------------------------------------ *
   * Components                                                          *
   * ------------------------------------------------------------------ */

  /** Every hole occupied by a lead, as a {holeIdx: componentId} map. */
  Board.prototype.occupancy = function (exceptId) {
    var occ = {};
    for (var i = 0; i < this.components.length; i++) {
      var c = this.components[i];
      if (exceptId != null && c.id === exceptId) continue;
      for (var p = 0; p < c.holes.length; p++) occ[c.holes[p]] = c.id;
    }
    return occ;
  };

  Board.prototype.componentAtHole = function (h) {
    // top-most first: a chip seated IN a socket shares the socket's holes, and
    // tapping there must grab the chip (placed later), not the socket under it
    for (var i = this.components.length - 1; i >= 0; i--) {
      if (this.components[i].holes.indexOf(h) >= 0) return this.components[i];
    }
    return null;
  };

  /**
   * Component whose BODY covers a world point — not just its leg holes. A wide part (a
   * resistor spans four holes, a capacitor two) has its body sitting midway BETWEEN the
   * holes, further than a hole's tap radius, so tapping the obvious thing — the body —
   * used to select nothing (Ivan 2026-07-24: "касание резистора и конденсатора не
   * реагирует, по крайней мере с телефона"). Hit-test the holes' bounding box grown by a
   * finger's worth of slack; iterate top-most first so a part on top wins.
   * @return {object|null}
   */
  Board.prototype.componentAtXY = function (wx, wy, pad) {
    if (pad == null) pad = PITCH * 0.6;
    for (var i = this.components.length - 1; i >= 0; i--) {
      var hs = this.components[i].holes, minx = Infinity, miny = Infinity, maxx = -Infinity, maxy = -Infinity;
      for (var k = 0; k < hs.length; k++) {
        var p = this.holeXY(hs[k]);
        if (p.x < minx) minx = p.x; if (p.x > maxx) maxx = p.x;
        if (p.y < miny) miny = p.y; if (p.y > maxy) maxy = p.y;
      }
      if (wx >= minx - pad && wx <= maxx + pad && wy >= miny - pad && wy <= maxy + pad) return this.components[i];
    }
    return null;
  };

  /** The part's leg hole nearest a world point — the grab anchor when a drag starts on the body. */
  Board.prototype.nearestHoleOf = function (c, wx, wy) {
    var best = c.holes[0], bd = Infinity;
    for (var k = 0; k < c.holes.length; k++) {
      var p = this.holeXY(c.holes[k]), d = (p.x - wx) * (p.x - wx) + (p.y - wy) * (p.y - wy);
      if (d < bd) { bd = d; best = c.holes[k]; }
    }
    return best;
  };

  /**
   * Leg holes for a part dropped with its first leg at (c0,r0).
   *
   * A part with more legs than it has span — the smart block has 6 legs and only ever
   * had a span of 2 — cannot lay them in a single row without two legs landing in the
   * same hole, which is exactly why placing it failed with "those holes are taken" on a
   * board the user had just cleared. So a multi-row DIP footprint gets its own layout:
   * half the legs on the near row, half on the far row, one row apart. Everything else
   * spreads evenly along a single row as before.
   *
   * @param {object} spec {pins, span, layout?}  layout:'dip' → two rows
   * @return {number[]|null} hole indices, or null if it runs off the board
   */
  Board.prototype.legHoles = function (spec, c0, r0) {
    var holes = [], i;
    if (spec.layout === 'dip') {
      var per = Math.ceil(spec.pins / 2), gap = spec.rowGap || 3;
      // near row left→right, far row left→right (pin order isn't load-bearing here —
      // the smart block addresses its legs by index, and the netlist bridge is
      // position-agnostic). Keeping both rows L→R makes the on-board labels readable.
      for (i = 0; i < per; i++) { if (!this.inBounds(c0 + i, r0)) return null; holes.push(this.idx(c0 + i, r0)); }
      for (i = 0; i < spec.pins - per; i++) { if (!this.inBounds(c0 + i, r0 + gap)) return null; holes.push(this.idx(c0 + i, r0 + gap)); }
      return holes;
    }
    for (i = 0; i < spec.pins; i++) {
      var off = spec.pins === 1 ? 0 : Math.round(i * spec.span / (spec.pins - 1));
      if (!this.inBounds(c0 + off, r0)) return null;
      holes.push(this.idx(c0 + off, r0));
    }
    return holes;
  };

  /** Bounding size of a footprint in holes, for the auto-placer's row/column budgeting. */
  Board.prototype.legFootprint = function (spec) {
    if (spec.layout === 'dip') return { w: Math.ceil(spec.pins / 2), h: (spec.rowGap || 3) + 1 };
    return { w: Math.max(1, spec.span + 1), h: 1 };
  };

  /** @return {boolean} every hole free and on the board. */
  // A chip may be SEATED into its socket — the one occupancy exception (Ivan
  // 31.07: «панелька есть, не пойми зачем! Почему на нее нельзя поставить
  // микросхему, если она для этого и предназначена?»).
  Board.SEATS = { chip8: 'socket8', chip14: 'socket14', chip16: 'socket16' };

  Board.prototype.canPlace = function (holes, exceptId, type) {
    var occ = this.occupancy(exceptId), seen = {};
    var blockers = {}, blocked = false;
    for (var i = 0; i < holes.length; i++) {
      var h = holes[i];
      if (!(h >= 0 && h < this.holeCount())) return false;
      if (occ[h] != null) { blocked = true; blockers[occ[h]] = 1; }
      if (seen[h]) return false;                 // a part cannot put two legs in one hole
      seen[h] = 1;
    }
    if (!blocked) return true;
    // seating: EVERY blocked hole belongs to exactly ONE part, that part is the
    // matching empty socket, and its holes coincide leg-for-leg with the chip's
    if (!type || !Board.SEATS[type]) return false;
    var ids = Object.keys(blockers);
    if (ids.length !== 1) return false;          // socket already holds a chip, or a stranger leg
    var sock = null;
    for (i = 0; i < this.components.length; i++) if (this.components[i].id === ids[0]) sock = this.components[i];
    if (!sock || sock.type !== Board.SEATS[type]) return false;
    if (sock.holes.length !== holes.length) return false;
    for (i = 0; i < holes.length; i++) if (sock.holes.indexOf(holes[i]) < 0) return false;
    return true;
  };

  /* ------------------------------------------------------------------ *
   * Electrical model — pads, and what joins them                        *
   * ------------------------------------------------------------------ */

  function padKey(layer, hole) { return layer + '#' + hole; }
  Board.padKey = padKey;

  // «Монтаж» — детали без электрической модели (панельки, гребёнки, клеммники,
  // модули): паяются и занимают дырки, но netlist() их пропускает, а связность
  // их ноги НЕ объединяют (каждый вывод панельки — отдельная точка; объединяет
  // пады только jumper). Список живёт здесь, потому что решает НЕ ui, а мост в
  // движок; ui.js CAT помечает те же типы mount:true для палитры и рендера.
  Board.MOUNT_TYPES = {
    socket8: 1, socket14: 1, socket16: 1,
    chip8: 1, chip14: 1, chip16: 1,
    header4: 1, header8: 1,
    terminal2: 1, terminal3: 1,
    module4: 1,
  };

  function makeDSU() {
    var parent = {};
    function find(x) {
      while (parent[x] != null && parent[x] !== x) { parent[x] = parent[parent[x]] != null ? parent[parent[x]] : parent[x]; x = parent[x]; }
      return x;
    }
    function uf(x) { if (parent[x] == null) parent[x] = x; return find(x); }
    function union(a, b) { var ra = uf(a), rb = uf(b); if (ra !== rb) parent[rb] = ra; }
    return { uf: uf, union: union };
  }

  /**
   * Union-find over every pad on the board.
   * Order matters only for readability — union is commutative.
   */
  Board.prototype.connectivity = function () {
    var dsu = makeDSU(), i, c, r, a, b;

    // 1. stripboard: each row of the BOTTOM layer is pre-joined, minus the cuts
    if (this.kind === 'strip') {
      for (r = 0; r < this.rows; r++) {
        for (c = 0; c + 1 < this.cols; c++) {
          a = this.idx(c, r); b = this.idx(c + 1, r);
          if (!this.hasCut(a, b)) dsu.union(padKey(0, a), padKey(0, b));
        }
      }
    }

    // 2. insulated top-side wire jumpers: their legs go through the holes and short
    //    the two pads regardless of the board being one- or two-sided
    for (i = 0; i < this.components.length; i++) {
      var jc = this.components[i];
      if (jc.type === 'jumper' && jc.holes.length >= 2) dsu.union(padKey(0, jc.holes[0]), padKey(0, jc.holes[1]));
    }

    // 2b. traces the user (or the router) drew
    for (i = 0; i < this.traces.length; i++) {
      var t = this.traces[i];
      dsu.union(padKey(t.layer, t.a), padKey(t.layer, t.b));
    }

    // 3. anything physically through a hole bridges its two pads.
    //    On a one-sided board there is no top pad, so this is a no-op.
    if (this.sides === 2) {
      if (this.plated) {
        // factory plating: EVERY hole is through — vias and lead-bridging are subsumed
        var total = this.holeCount();
        for (i = 0; i < total; i++) dsu.union(padKey(0, i), padKey(1, i));
      } else {
        for (i = 0; i < this.vias.length; i++) dsu.union(padKey(0, this.vias[i]), padKey(1, this.vias[i]));
        for (i = 0; i < this.components.length; i++) {
          var comp = this.components[i];
          for (var p = 0; p < comp.holes.length; p++) dsu.union(padKey(0, comp.holes[p]), padKey(1, comp.holes[p]));
        }
      }
    }

    return dsu;
  };

  /**
   * Bridge the board into the shape CircuitEngine.simulate() wants.
   * Identical contract to the breadboard's _netlist(): {components, wires}.
   */
  Board.prototype.netlist = function () {
    var dsu = this.connectivity(), nets = {}, comps = [], i, p;

    for (i = 0; i < this.components.length; i++) {
      var c = this.components[i];
      if (c.type === 'jumper') continue;   // pure wiring — already merged in connectivity()
      if (Board.MOUNT_TYPES[c.type]) continue;  // mount-only hardware — no electrical model
      var o = { id: c.id, type: c.type };
      for (var k in c.props) { if (Object.prototype.hasOwnProperty.call(c.props, k)) o[k] = c.props[k]; }
      comps.push(o);
      for (p = 0; p < c.holes.length; p++) {
        var rootKey = dsu.uf(padKey(0, c.holes[p]));
        (nets[rootKey] || (nets[rootKey] = [])).push({ c: c.id, p: p });
      }
    }

    // chain every pin on a net back to the first one — the engine only needs
    // the equivalence, not a particular tree
    var wires = [];
    for (var rk in nets) {
      if (!Object.prototype.hasOwnProperty.call(nets, rk)) continue;
      var pins = nets[rk];
      for (i = 1; i < pins.length; i++) wires.push({ a: pins[0], b: pins[i] });
    }
    return { components: comps, wires: wires };
  };

  /**
   * Which target connections are still missing / accidentally shorted.
   * `target` is a netlist in the same shape the importer produces:
   *   {nets: [[{c,p},...], ...]}  — each entry is one required equipotential group.
   * Returns {ok, missing:[[pinA,pinB]], shorts:[[pinA,pinB]]}.
   *
   * This is the "did I wire it right?" check the whole tool exists for, so it is
   * deliberately symmetric: a pair that SHOULD be joined and isn't is `missing`,
   * a pair that should NOT be and is, is a `short`.
   */
  Board.prototype.verify = function (target) {
    var dsu = this.connectivity(), i, j, res = { ok: true, missing: [], shorts: [] };
    if (!target || !target.nets) return res;

    var pinRoot = {};   // "compId/pin" -> pad root
    for (i = 0; i < this.components.length; i++) {
      var c = this.components[i];
      for (var p = 0; p < c.holes.length; p++) pinRoot[c.id + '/' + p] = dsu.uf(padKey(0, c.holes[p]));
    }

    var groupOf = {};   // "compId/pin" -> target net index
    for (i = 0; i < target.nets.length; i++) {
      for (j = 0; j < target.nets[i].length; j++) {
        var pin = target.nets[i][j];
        groupOf[pin.c + '/' + pin.p] = i;
      }
    }

    // missing: two pins in the same target net whose pads are not joined
    for (i = 0; i < target.nets.length; i++) {
      var grp = target.nets[i];
      for (j = 1; j < grp.length; j++) {
        var k0 = grp[0].c + '/' + grp[0].p, kj = grp[j].c + '/' + grp[j].p;
        if (pinRoot[k0] == null || pinRoot[kj] == null) continue;   // not placed yet
        if (pinRoot[k0] !== pinRoot[kj]) { res.missing.push([grp[0], grp[j]]); res.ok = false; }
      }
    }

    // shorts: pads joined across two different target nets
    var byRoot = {};
    for (var pk in pinRoot) {
      if (!Object.prototype.hasOwnProperty.call(pinRoot, pk)) continue;
      var g = groupOf[pk];
      if (g == null) continue;
      var rt = pinRoot[pk];
      if (byRoot[rt] == null) { byRoot[rt] = { g: g, pk: pk }; }
      else if (byRoot[rt].g !== g) {
        res.shorts.push([parsePin(byRoot[rt].pk), parsePin(pk)]);
        res.ok = false;
      }
    }
    return res;
  };

  function parsePin(k) {
    var i = k.lastIndexOf('/');
    return { c: k.slice(0, i), p: parseInt(k.slice(i + 1), 10) };
  }

  /* ------------------------------------------------------------------ *
   * Autorouter — Lee / maze search with pad exclusion                    *
   * ------------------------------------------------------------------ */

  /**
   * Route one net: join every pin-hole in `holes` into a single connected set.
   *
   * Grows the net incrementally — after the first leg is laid, later legs may end
   * on ANY pad the net already owns, which is what gives Steiner-shaped trees for
   * free instead of a star of overlapping paths.
   *
   * @param {number[]} holes    pin holes to join
   * @param {object}   blocked  {padKey: true} pads owned by other nets
   * @param {object} [opt]      {preferLayer}
   * @return {{ok:boolean, traces:Array, vias:number[], used:object}}
   */
  Board.prototype.routeNet = function (holes, blocked, opt) {
    opt = opt || {};
    blocked = blocked || {};
    var self = this, maxL = this.maxLayer();
    var out = { ok: true, traces: [], vias: [], used: {} };
    if (!holes || holes.length < 2) {
      for (var q = 0; q < (holes || []).length; q++) out.used[padKey(0, holes[q])] = true;
      return out;
    }

    // the net starts owning the pads of its own first pin.
    // On a PLATED board the two pads of a hole are one electrical point, so
    // owning either side owns both — otherwise a rival net would claim the
    // "free" top pad of a hole this net already runs through and short it.
    var plated = this.plated;
    var owned = {};
    function own(layer, hole) {
      owned[padKey(layer, hole)] = true;
      if (plated) for (var l2 = 0; l2 <= maxL; l2++) owned[padKey(l2, hole)] = true;
    }
    function ownAllLayers(hole) { for (var l = 0; l <= maxL; l++) own(l, hole); }

    ownAllLayers(holes[0]);

    for (var i = 1; i < holes.length; i++) {
      var target = holes[i];
      var path = this._maze(owned, target, blocked, opt);
      if (!path) { out.ok = false; continue; }
      // path is a list of {layer, hole} from an owned pad to the target
      for (var s = 0; s + 1 < path.length; s++) {
        var a = path[s], b = path[s + 1];
        if (a.hole === b.hole) {
          // layer change at a hole = a soldered via; on a plated board the
          // plating IS the via — nothing to solder, nothing to record
          if (!plated && out.vias.indexOf(a.hole) < 0) out.vias.push(a.hole);
        } else {
          out.traces.push({ layer: a.layer, a: Math.min(a.hole, b.hole), b: Math.max(a.hole, b.hole) });
        }
        own(a.layer, a.hole); own(b.layer, b.hole);
      }
      ownAllLayers(target);
    }

    for (var k in owned) if (Object.prototype.hasOwnProperty.call(owned, k)) out.used[k] = true;
    return out;
  };

  /**
   * Breadth-first search from the whole `owned` frontier to `target`.
   * Uniform edge cost with a small via penalty expressed as a deferred queue —
   * a plain BFS with a 2-bucket queue is enough here and keeps this dependency-free.
   */
  Board.prototype._maze = function (owned, target, blocked, opt) {
    var maxL = this.maxLayer(), self = this;
    var prev = {}, seen = {}, queue = [], head = 0;

    function passable(layer, hole) {
      var pk = padKey(layer, hole);
      if (blocked[pk]) return false;
      // a pad holding somebody else's component lead is not ours to cross
      var comp = self.componentAtHole(hole);
      if (comp && !owned[padKey(0, hole)] && hole !== target) return false;
      return true;
    }

    for (var k in owned) {
      if (!Object.prototype.hasOwnProperty.call(owned, k)) continue;
      if (seen[k]) continue;
      seen[k] = true; queue.push(k);
    }
    if (!queue.length) return null;

    var preferred = opt.preferLayer != null ? opt.preferLayer : 0;

    while (head < queue.length) {
      var cur = queue[head++];
      var sp = cur.indexOf('#');
      var layer = parseInt(cur.slice(0, sp), 10), hole = parseInt(cur.slice(sp + 1), 10);

      if (hole === target) return rebuild(prev, cur);

      // same-layer steps to adjacent holes
      var nb = this.neighbours(hole);
      for (var n = 0; n < nb.length; n++) {
        var nk = padKey(layer, nb[n]);
        if (seen[nk] || !passable(layer, nb[n])) continue;
        seen[nk] = true; prev[nk] = cur; queue.push(nk);
      }
      // layer change (via) — only where the hole is free on the other side too
      if (maxL > 0) {
        var other = layer === 0 ? 1 : 0;
        var ok2 = padKey(other, hole);
        if (!seen[ok2] && passable(other, hole)) {
          seen[ok2] = true; prev[ok2] = cur;
          // deprioritise vias by appending late: BFS order already favours the
          // shorter same-layer route, and this keeps the queue single-pass
          queue.push(ok2);
        }
      }
      void preferred;
    }
    return null;
  };

  function rebuild(prev, endKey) {
    var path = [], cur = endKey;
    while (cur != null) {
      var sp = cur.indexOf('#');
      path.push({ layer: parseInt(cur.slice(0, sp), 10), hole: parseInt(cur.slice(sp + 1), 10) });
      cur = prev[cur];
    }
    path.reverse();
    return path;
  }

  /**
   * Route the whole board from a target netlist, laying real traces/vias.
   *
   * Order matters enormously and no single order is right. Short-first keeps sprawling
   * power nets from carving up the board — but a short net that grabs the one escape
   * lane can box a long net in completely, with the board still half empty. (Real case:
   * a 3-part series loop on the default board, where the two short legs took the only
   * way around and the return leg had literally no path to its own pin.)
   *
   * So this is rip-up-and-retry: route, and if anything failed, start over with the
   * failed nets FIRST. Two or three passes settle almost everything, and we keep the
   * best board seen rather than whatever the last pass happened to produce.
   *
   * @param {object} target {nets:[[{c,p},...]]}
   * @return {{ok:boolean, routed:number, failed:Array}}
   */
  Board.prototype.autoroute = function (target) {
    var i, j;
    var pinHole = {};
    for (i = 0; i < this.components.length; i++) {
      var c = this.components[i];
      for (j = 0; j < c.holes.length; j++) pinHole[c.id + '/' + j] = c.holes[j];
    }

    var jobs = [];
    for (i = 0; i < target.nets.length; i++) {
      var holes = [], grp = target.nets[i];
      for (j = 0; j < grp.length; j++) {
        var h = pinHole[grp[j].c + '/' + grp[j].p];
        if (h != null && holes.indexOf(h) < 0) holes.push(h);
      }
      if (holes.length >= 2) jobs.push({ net: i, holes: holes, span: spanOf(this, holes) });
    }
    jobs.sort(function (a, b) { return a.span - b.span; });

    var MAX_PASSES = 4;
    var best = null;
    for (var pass = 0; pass < MAX_PASSES; pass++) {
      var attempt = this._routePass(jobs);
      if (!best || attempt.res.routed > best.res.routed) best = attempt;
      if (attempt.res.ok) break;
      // next pass: whatever failed goes first, so it picks its path while the board is empty
      var failedSet = {};
      for (i = 0; i < attempt.res.failed.length; i++) failedSet[attempt.res.failed[i]] = true;
      var head = [], tail = [];
      for (i = 0; i < jobs.length; i++) (failedSet[jobs[i].net] ? head : tail).push(jobs[i]);
      if (!head.length) break;                       // nothing to promote — reordering cannot help
      jobs = head.concat(tail);
    }

    this.traces = best.traces;
    this.vias = best.vias;
    return best.res;
  };

  /** One routing pass in the given net order. Does not mutate the board. */
  Board.prototype._routePass = function (jobs) {
    var traces = [], vias = [], blocked = {}, res = { ok: true, routed: 0, failed: [] }, i, j;
    for (i = 0; i < jobs.length; i++) {
      var r = this.routeNet(jobs[i].holes, blocked);
      for (j = 0; j < r.traces.length; j++) traces.push(r.traces[j]);
      for (j = 0; j < r.vias.length; j++) if (vias.indexOf(r.vias[j]) < 0) vias.push(r.vias[j]);
      for (var k in r.used) if (Object.prototype.hasOwnProperty.call(r.used, k)) blocked[k] = true;
      if (r.ok) res.routed++; else { res.ok = false; res.failed.push(jobs[i].net); }
    }
    return { traces: traces, vias: vias, res: res };
  };

  /**
   * Re-route the copper after a part was MOVED by hand (Ivan 30.07: «дорожки не
   * тянутся деталями… хочется передвигать детали, а дорожки на месте остаются,
   * так что смысла 0»). Auto-reroute existed only for imported boards (a known
   * target netlist); a hand-wired board left its traces where they were.
   *
   * For every leg that changed hole: find the trace-connected copper island the
   * OLD hole belonged to, remember every pin that island touched, and lay it
   * again from the NEW hole to those same pins. Route first, mutate after — a
   * failed route falls back to an insulated wire jumper (the same honest cure
   * autolayout uses for non-planar nets) instead of silently dropping copper.
   *
   * @param {string} movedId component id (already sitting at its NEW holes)
   * @param {number[]} oldHoles the holes it occupied before the move
   * @return {boolean} true when anything was re-laid
   */
  Board.prototype.retraceMoved = function (movedId, oldHoles) {
    var self = this, i, j;
    if (!this.traces.length) return false;
    var comp = null;
    for (i = 0; i < this.components.length; i++) if (this.components[i].id === movedId) comp = this.components[i];
    if (!comp) return false;

    // copper islands: union pads over TRACES (+vias across layers), nothing else
    var dsu = makeDSU();
    for (i = 0; i < this.traces.length; i++) { var tr = this.traces[i]; dsu.union(padKey(tr.layer, tr.a), padKey(tr.layer, tr.b)); }
    for (i = 0; i < this.vias.length; i++) dsu.union(padKey(0, this.vias[i]), padKey(1, this.vias[i]));

    // which islands are impacted, and where each moved leg went
    var islands = {};   // root -> { newHoles: [], oldSet: {} }
    for (i = 0; i < oldHoles.length; i++) {
      if (oldHoles[i] === comp.holes[i]) continue;
      // hole still physically occupied (a chip lifted OUT of its socket leaves the
      // socket's legs behind) → the copper there still connects to something; keep it
      if (this.componentAtHole(oldHoles[i])) continue;
      var rk = dsu.uf(padKey(0, oldHoles[i]));
      var isl = islands[rk] || (islands[rk] = { newHoles: [], oldSet: {} });
      isl.newHoles.push(comp.holes[i]);
      isl.oldSet[oldHoles[i]] = true;
    }
    var roots = Object.keys(islands);
    if (!roots.length) return false;

    // stationary pins each island still touches: every part's CURRENT holes minus
    // the abandoned ones. A rotation moves SOME legs — the pivot leg stays put and
    // must stay in the endpoint list, so filter by abandoned HOLE, not by part id.
    for (i = 0; i < this.components.length; i++) {
      var c = this.components[i];
      for (j = 0; j < c.holes.length; j++) {
        var pk = dsu.uf(padKey(0, c.holes[j]));
        if (islands[pk] && !islands[pk].oldSet[c.holes[j]]) islands[pk].newHoles.push(c.holes[j]);
      }
    }

    // freeze island membership BEFORE any mutation — re-deriving the DSU mid-loop
    // would rename the roots and desync the removal filter for the second island
    for (i = 0; i < this.traces.length; i++) {
      var tt = this.traces[i], trk = dsu.uf(padKey(tt.layer, tt.a));
      if (islands[trk]) (islands[trk].traces || (islands[trk].traces = [])).push(tt);
    }
    for (i = 0; i < this.vias.length; i++) {
      var vrk = dsu.uf(padKey(0, this.vias[i]));
      if (islands[vrk]) (islands[vrk].vias || (islands[vrk].vias = [])).push(this.vias[i]);
    }

    var changed = false;
    for (i = 0; i < roots.length; i++) {
      var island = islands[roots[i]];
      var holes = [];
      for (j = 0; j < island.newHoles.length; j++) if (holes.indexOf(island.newHoles[j]) < 0) holes.push(island.newHoles[j]);
      if (holes.length < 2) continue;                     // island touched nothing else — leave it

      var drop = island.traces || [], dropV = island.vias || [];
      var keepTraces = this.traces.filter(function (t2) { return drop.indexOf(t2) < 0; });
      var keepVias = this.vias.filter(function (v2) { return dropV.indexOf(v2) < 0; });
      // pads owned by the copper we KEEP are off-limits for the new route
      var blocked = {};
      for (j = 0; j < keepTraces.length; j++) { blocked[padKey(keepTraces[j].layer, keepTraces[j].a)] = true; blocked[padKey(keepTraces[j].layer, keepTraces[j].b)] = true; }

      this.traces = keepTraces; this.vias = keepVias;    // route against the board WITHOUT the island
      var r = this.routeNet(holes, blocked);
      if (r.ok) {
        for (j = 0; j < r.traces.length; j++) this.traces.push(r.traces[j]);
        for (j = 0; j < r.vias.length; j++) if (this.vias.indexOf(r.vias[j]) < 0) this.vias.push(r.vias[j]);
      } else {
        // copper cannot reach — bridge with insulated jumpers, never drop the net
        for (j = 1; j < holes.length; j++) {
          this.components.push({ id: 'p' + (this.nextId++), type: 'jumper', ref: '', holes: [holes[0], holes[j]], props: {} });
        }
      }
      changed = true;
    }
    return changed;
  };

  /** Bounding-box perimeter of a net's pins — the ordering heuristic for autoroute. */
  function spanOf(board, holes) {
    var minC = 1e9, maxC = -1e9, minR = 1e9, maxR = -1e9;
    for (var i = 0; i < holes.length; i++) {
      var c = board.col(holes[i]), r = board.row(holes[i]);
      if (c < minC) minC = c; if (c > maxC) maxC = c;
      if (r < minR) minR = r; if (r > maxR) maxR = r;
    }
    return (maxC - minC) + (maxR - minR);
  }

  /* ------------------------------------------------------------------ *
   * Placement — decide WHERE parts sit before the router runs           *
   * ------------------------------------------------------------------ */

  /**
   * Lay components out left-to-right in rows, leaving a blank row between bands so
   * the router has somewhere to go. Parts that share a net are pulled together by
   * ordering on a cheap connectivity walk rather than a full force-directed pass —
   * good enough to make the router's job tractable, and predictable for the user.
   *
   * @param {Array} specs [{type, pins, span}]  span = holes between first and last leg
   * @param {object} target netlist, used only for ordering
   * @return {boolean} true when everything fit
   */
  Board.prototype.autoplace = function (specs, target) {
    var order = orderByConnectivity(specs, target);

    // Aim for a roughly SQUARE block, not one long band. Wrapping only at the board
    // edge laid an 11-part multivibrator as a single 49-hole strip across row 1 with
    // the routes snaking the full width — electrically fine, visually a mess the user
    // called "ужас" (Ivan #6384). A band costs ~2 rows, so a block of total width W
    // is balanced at bandW ≈ sqrt(2W); if that does not fit we retry wider, ending at
    // the full board width, which is exactly the old behaviour.
    var totalW = 0, maxW = 1, i;
    for (i = 0; i < order.length; i++) {
      var f = this.legFootprint(order[i]);
      totalW += f.w + 1;
      if (f.w > maxW) maxW = f.w;
    }
    var widths = this._bandWidths(totalW, maxW);
    for (i = 0; i < widths.length; i++) {
      if (this._placePass(order, widths[i])) return true;
    }
    return false;
  };

  /** Candidate band widths, tightest first, always ending at the full board width. */
  Board.prototype._bandWidths = function (totalW, maxW) {
    var ideal = Math.min(this.cols, Math.max(maxW, Math.ceil(Math.sqrt(2 * totalW))));
    var widths = [ideal], step = ideal;
    while (step < this.cols) {
      step = Math.min(this.cols, step + Math.max(3, Math.ceil(ideal / 2)));
      widths.push(step);
    }
    return widths;
  };

  /**
   * Placement AND routing in one decision. The tightest band that PLACES is not
   * always the one that ROUTES: the compact multivibrator block left one MCU net
   * with no corridor no matter which rip-up order the router tried, while two
   * columns more of elbow room routed everything. So: try widths tightest-first,
   * accept the first fully-routed layout, and if none routes completely keep the
   * best one seen rather than the last.
   *
   * @return {{placed:boolean, route:{ok:boolean, routed:number, failed:Array}}}
   */
  Board.prototype.autolayout = function (specs, target) {
    var order = orderByConnectivity(specs, target);
    var totalW = 0, maxW = 1, i;
    for (i = 0; i < order.length; i++) {
      var f = this.legFootprint(order[i]);
      totalW += f.w + 1;
      if (f.w > maxW) maxW = f.w;
    }
    var widths = this._bandWidths(totalW, maxW);
    var best = null;
    for (i = 0; i < widths.length; i++) {
      if (!this._placePass(order, widths[i])) continue;
      var route = this.autoroute(target);
      if (route.ok) return { placed: true, route: route, jumpers: 0 };
      if (!best || route.routed > best.route.routed) {
        best = { route: route, components: this.components, nextId: this.nextId, traces: this.traces, vias: this.vias };
      }
    }
    if (!best) { this.components = []; this.traces = []; this.vias = []; return { placed: false, route: { ok: false, routed: 0, failed: [] }, jumpers: 0 }; }
    this.components = best.components; this.nextId = best.nextId;
    this.traces = best.traces; this.vias = best.vias;
    var jumpers = this.bridgeMissing(target);
    return { placed: true, route: best.route, jumpers: jumpers };
  };

  /**
   * Whatever the copper could not finish, finish with insulated wire jumpers —
   * exactly what a real one-sided perfboard build does when two traces would have
   * to cross. A multivibrator is non-planar: no rip-up order routes all eight nets
   * on one copper layer, and the old behaviour shipped the board with a warning
   * and silent gaps. The jumper's legs SHARE the pads of the pins they join —
   * soldering a wire onto an occupied pad is normal bench practice, and
   * connectivity() unions pads, so the electrical model stays exact.
   */
  Board.prototype.bridgeMissing = function (target) {
    var added = 0, guard = 0;
    while (guard++ < 64) {
      var v = this.verify(target);
      if (!v.missing.length) break;
      var pair = v.missing[0];
      var ha = this.holeOfPin(pair[0].c, pair[0].p), hb = this.holeOfPin(pair[1].c, pair[1].p);
      if (ha == null || hb == null || ha === hb) break;
      this.components.push({ id: 'p' + (this.nextId++), type: 'jumper', ref: '', holes: [ha, hb], props: {} });
      added++;
    }
    return added;
  };

  Board.prototype.holeOfPin = function (cid, p) {
    for (var i = 0; i < this.components.length; i++) {
      if (this.components[i].id === cid) return this.components[i].holes[p];
    }
    return null;
  };

  /** One placement attempt at the given band width. Leaves its result on the board. */
  Board.prototype._placePass = function (order, bandW) {
    // Start on row 1, not row 0. A band hugging the top edge has no lane ABOVE it, so
    // a return net that has to get past the parts is left with one side instead of two
    // — which is precisely how a three-part series loop ended up unroutable on an
    // otherwise empty board.
    var c = 0, r = this.rows > 2 ? 1 : 0, bandH = 1;
    this.components = [];
    this.nextId = 1;

    for (var n = 0; n < order.length; n++) {
      var s = order[n];
      var fp = this.legFootprint(s);
      // blank row between bands; a band is as tall as its tallest part (the smart
      // block's DIP body spans 4 rows — advancing a flat 2 would land the next band
      // inside it and fail the whole pass on a phantom collision)
      if (c > 0 && c + fp.w > bandW) { c = 0; r += bandH + 1; bandH = 1; }
      if (fp.h > bandH) bandH = fp.h;
      if (r + fp.h - 1 >= this.rows) return false;
      var holes = this.legHoles(s, c, r);
      if (!holes || !this.canPlace(holes)) return false;
      this.components.push({
        id: s.id || ('p' + (this.nextId++)),
        type: s.type, ref: s.ref || '', holes: holes,
        props: s.props ? JSON.parse(JSON.stringify(s.props)) : {}
      });
      c += fp.w + 1;
    }
    return true;
  };

  /** Depth-first walk of the "shares a net" graph, so neighbours land near each other. */
  function orderByConnectivity(specs, target) {
    if (!target || !target.nets) return specs.slice();
    var byId = {}, i, j;
    for (i = 0; i < specs.length; i++) byId[specs[i].id] = specs[i];

    var adj = {};
    for (i = 0; i < target.nets.length; i++) {
      var grp = target.nets[i];
      for (j = 0; j < grp.length; j++) {
        for (var k = j + 1; k < grp.length; k++) {
          (adj[grp[j].c] || (adj[grp[j].c] = [])).push(grp[k].c);
          (adj[grp[k].c] || (adj[grp[k].c] = [])).push(grp[j].c);
        }
      }
    }
    var seen = {}, out = [];
    function walk(id) {
      if (seen[id] || !byId[id]) return;
      seen[id] = true; out.push(byId[id]);
      var nb = adj[id] || [];
      for (var x = 0; x < nb.length; x++) walk(nb[x]);
    }
    // start from the part with the most connections — usually the battery or the IC
    var ids = specs.map(function (s) { return s.id; }).sort(function (a, b) {
      return (adj[b] ? adj[b].length : 0) - (adj[a] ? adj[a].length : 0);
    });
    for (i = 0; i < ids.length; i++) walk(ids[i]);
    for (i = 0; i < specs.length; i++) if (!seen[specs[i].id]) out.push(specs[i]);
    return out;
  }

  /* ------------------------------------------------------------------ *
   * Serialisation — share links, autosave, undo snapshots               *
   * ------------------------------------------------------------------ */

  Board.prototype.serialize = function () {
    return {
      v: 1, cols: this.cols, rows: this.rows, kind: this.kind, sides: this.sides,
      pl: this.plated ? 1 : 0,
      t: this.traces.map(function (t) { return [t.layer, t.a, t.b]; }),
      x: this.cuts.map(function (c) { return [c.a, c.b]; }),
      vi: this.vias.slice(),
      n: this.notes,
      c: this.components.map(function (c) {
        var p = {};
        for (var k in c.props) { if (k.charAt(0) !== '_') p[k] = c.props[k]; }
        return { t: c.type, h: c.holes, r: c.ref, p: p };
      })
    };
  };

  Board.deserialize = function (d) {
    if (!d || !d.cols) return null;
    // old saves carry no `pl` — they deserialize as unplated, unchanged behaviour
    var b = new Board({ cols: d.cols, rows: d.rows, kind: d.kind, sides: d.sides, plated: !!d.pl });
    b.traces = (d.t || []).map(function (a) { return { layer: a[0], a: a[1], b: a[2] }; });
    b.cuts = (d.x || []).map(function (a) { return { a: a[0], b: a[1] }; });
    b.vias = (d.vi || []).slice();
    b.notes = (d.n && typeof d.n === 'object') ? d.n : {};
    b.components = (d.c || []).map(function (o, i) {
      return { id: 'p' + (i + 1), type: o.t, ref: o.r || '', holes: o.h || [], props: o.p || {} };
    });
    b.nextId = b.components.length + 1;
    return b;
  };

  /* ------------------------------------------------------------------ *
   * Export                                                              *
   * ------------------------------------------------------------------ */

  if (typeof module !== 'undefined' && module.exports) module.exports = { Board: Board };

  // MERGE into the namespace, never replace it: import.js publishes PerfBoard.importer
  // and whichever file loads second would otherwise wipe the other. Assigning the whole
  // object made the app depend on script-tag order — a bomb that only goes off the day
  // someone reorders the blade.
  var ns = root.PerfBoard || (root.PerfBoard = {});
  ns.Board = Board;

  // globalThis, not window — board.test.mjs imports this file under node, where the
  // engine next door binds the same way (see circuit-simulator/engine.js).
})(typeof globalThis !== 'undefined' ? globalThis : this);
